/**
 * System updates — one-click updates of this Eurion server.
 *
 * Talks to admin-service /v1/admin/system/updates, which forwards to the host updater.
 * Installing, scheduling and uploading need a super admin who re-entered their password
 * (step-up token, 5 minutes). During an install the services restart, so the API is
 * briefly unreachable: the page keeps polling and shows that instead of an error.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { Download, RefreshCw, CheckCircle2, AlertTriangle, XCircle, Clock, Upload, ScrollText, Loader2 } from 'lucide-react';

interface Bundle { file?: string; from_version?: string; size?: number; sha256?: string }
interface Offer { version: string; notes?: string; published_at?: string; source: 'channel' | 'upload'; bundle?: Bundle | null; installable: boolean; reason?: string | null }
interface Job { id?: string; version: string; from?: string; status: 'downloading' | 'verifying' | 'installing' | 'done' | 'rolled_back' | 'failed'; phase?: string | null; message?: string | null; started_at?: string; finished_at?: string | null; actor?: string }
interface Status {
  current_version: string | null; channel_url: string | null; auto_check: boolean;
  last_check: string | null; last_check_error: string | null;
  available: Offer | null; job: Job | null; schedule: { version: string; at: string } | null;
}

const BASE = '/v1/admin/system/updates';
const RUNNING: Job['status'][] = ['downloading', 'verifying', 'installing'];

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(BASE + path, init);
  const body = await res.json().catch(() => ({})) as { success?: boolean; data?: T; error?: { message?: string } };
  if (!res.ok || body.success === false) throw Object.assign(new Error(body.error?.message ?? `HTTP ${res.status}`), { status: res.status });
  return body.data as T;
}

function mb(n?: number) { return n ? `${Math.round(n / 1024 / 1024)} MB` : ''; }
function when(iso?: string | null) { return iso ? new Date(iso).toLocaleString() : '—'; }
function tonightAt2(): Date {
  const d = new Date(); d.setHours(2, 0, 0, 0);
  if (d.getTime() <= Date.now()) d.setDate(d.getDate() + 1);
  return d;
}

/** Small hook for the sidebar badge: is an update on offer? */
export function useUpdateAvailable(enabled: boolean): string | null {
  const [version, setVersion] = useState<string | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const load = () => api<Status>('').then(s => { if (alive) setVersion(s.available?.installable ? s.available.version : null); }).catch(() => {});
    load();
    const t = setInterval(load, 10 * 60 * 1000);
    return () => { alive = false; clearInterval(t); };
  }, [enabled]);
  return version;
}

export default function SystemUpdates({ isSuperAdmin }: { isSuperAdmin: boolean }) {
  const [status, setStatus] = useState<Status | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [unreachable, setUnreachable] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [log, setLog] = useState<string | null>(null);
  const [stepUp, setStepUp] = useState<{ token: string; until: number } | null>(null);
  const [askPassword, setAskPassword] = useState<null | ((token: string) => void)>(null);
  const [password, setPassword] = useState('');
  const [pwError, setPwError] = useState<string | null>(null);
  const [upload, setUpload] = useState<{ name: string; pct: number } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const running = !!status?.job && RUNNING.includes(status.job.status);

  const load = useCallback(async () => {
    try {
      setStatus(await api<Status>(''));
      setUnreachable(false); setError(null);
    } catch (e) {
      const st = (e as { status?: number }).status;
      // 502/503/504 or a network error while an install runs = services restarting
      if (!st || st >= 502) setUnreachable(true);
      else setError((e as Error).message);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    const t = setInterval(() => { void load(); }, running || unreachable ? 3000 : 30000);
    return () => clearInterval(t);
  }, [load, running, unreachable]);

  /** Run fn with a valid step-up token, asking for the password if needed. */
  const withStepUp = (fn: (token: string) => Promise<void>) => {
    if (stepUp && stepUp.until > Date.now() + 15_000) { void fn(stepUp.token); return; }
    setPassword(''); setPwError(null);
    setAskPassword(() => (token: string) => { void fn(token); });
  };

  const confirmPassword = async () => {
    try {
      const res = await fetch('/v1/auth/step-up', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password }) });
      const body = await res.json().catch(() => ({})) as { success?: boolean; data?: { stepUpToken: string; expiresIn: number }; error?: { message?: string } };
      if (!res.ok || !body.data) { setPwError(body.error?.message ?? 'Wrong password'); return; }
      const token = body.data.stepUpToken;
      setStepUp({ token, until: Date.now() + body.data.expiresIn * 1000 });
      const next = askPassword; setAskPassword(null); setPassword('');
      next?.(token);
    } catch { setPwError('Could not reach the server'); }
  };

  const act = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(label); setError(null);
    try { await fn(); await load(); } catch (e) { setError((e as Error).message); } finally { setBusy(null); }
  };

  const install = (version: string) => withStepUp(token => act('install', () =>
    api('/install', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Step-Up-Token': token }, body: JSON.stringify({ version }) })));

  const schedule = (version: string) => withStepUp(token => act('schedule', () =>
    api('/schedule', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Step-Up-Token': token }, body: JSON.stringify({ version, at: tonightAt2().toISOString() }) })));

  const startUpload = (file: File) => withStepUp(token => new Promise<void>((resolve) => {
    // XHR for upload progress (fetch has none); add the session token ourselves
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BASE}/upload`);
    xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('eurion_token') ?? ''}`);
    xhr.setRequestHeader('X-Step-Up-Token', token);
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) setUpload({ name: file.name, pct: Math.round(e.loaded * 100 / e.total) }); };
    xhr.onload = () => {
      setUpload(null);
      if (xhr.status >= 300) {
        let msg = `Upload failed (HTTP ${xhr.status})`;
        try { msg = (JSON.parse(xhr.responseText) as { error?: { message?: string } }).error?.message ?? msg; } catch { /* keep */ }
        setError(msg);
      }
      void load(); resolve();
    };
    xhr.onerror = () => { setUpload(null); setError('Upload failed: connection lost'); resolve(); };
    setUpload({ name: file.name, pct: 0 });
    xhr.send(file);
  }));

  const showLog = async () => {
    try { setLog((await api<{ log: string }>('/log')).log || '(empty)'); } catch (e) { setError((e as Error).message); }
  };

  if (!status && !unreachable && !error) {
    return <div className="p-8 text-sm text-nord-4"><Loader2 className="inline h-4 w-4 animate-spin" /> Loading…</div>;
  }

  const offer = status?.available;
  const job = status?.job;

  return (
    <div className="max-w-3xl space-y-5 p-8">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold text-nord-6">System updates</h1>
          <p className="mt-1 text-sm text-nord-4">
            Installed version <span className="font-mono text-nord-6">{status?.current_version ?? '…'}</span>
            {status?.channel_url ? <> · checks {status.auto_check ? 'daily' : 'manually'}</> : <> · offline server (upload updates below)</>}
          </p>
        </div>
        {isSuperAdmin && status?.channel_url && (
          <button disabled={!!busy || running} onClick={() => act('check', () => api('/check', { method: 'POST' }))}
            className="flex items-center gap-2 rounded-lg border border-nord-3 px-3 py-1.5 text-sm text-nord-5 hover:bg-nord-2 disabled:opacity-50">
            <RefreshCw className={`h-4 w-4 ${busy === 'check' ? 'animate-spin' : ''}`} /> Check now
          </button>
        )}
      </div>

      {unreachable && (
        <div className="flex items-center gap-3 rounded-xl border border-nord-13/40 bg-nord-13/10 p-4 text-sm text-nord-5">
          <Loader2 className="h-5 w-5 animate-spin text-nord-13" />
          {running || job?.status === 'installing'
            ? 'The services are restarting for the update. This page reconnects by itself, usually within a minute.'
            : 'Cannot reach the server right now. Retrying…'}
        </div>
      )}
      {error && <div className="rounded-xl border border-nord-11/40 bg-nord-11/10 p-4 text-sm text-nord-5">{error}</div>}
      {status?.last_check_error && !offer && (
        <div className="text-xs text-nord-12">Last check failed ({when(status.last_check)}): {status.last_check_error}</div>
      )}

      {/* Running or finished job */}
      {job && (
        <div className="rounded-xl border border-nord-2 bg-nord-1 p-5">
          <div className="flex items-center gap-3">
            {RUNNING.includes(job.status) && <Loader2 className="h-5 w-5 animate-spin text-nord-8" />}
            {job.status === 'done' && <CheckCircle2 className="h-5 w-5 text-nord-14" />}
            {job.status === 'rolled_back' && <AlertTriangle className="h-5 w-5 text-nord-13" />}
            {job.status === 'failed' && <XCircle className="h-5 w-5 text-nord-11" />}
            <div className="flex-1">
              <div className="font-semibold text-nord-6">
                {job.status === 'downloading' && `Downloading ${job.version}…`}
                {job.status === 'verifying' && `Checking the signature of ${job.version}…`}
                {job.status === 'installing' && `Installing ${job.version}${job.phase ? ` · step ${job.phase}` : ''}`}
                {job.status === 'done' && `Updated to ${job.version}`}
                {job.status === 'rolled_back' && `Update to ${job.version} failed, rolled back to ${job.from ?? 'the previous version'}`}
                {job.status === 'failed' && `Update to ${job.version} failed`}
              </div>
              <div className="mt-0.5 text-xs text-nord-4">
                {job.message ?? (job.status === 'installing' ? 'Backup, migrations and restart run automatically; a failure rolls back by itself.' : '')}
                {job.started_at && <> · started {when(job.started_at)}</>}{job.finished_at && <> · finished {when(job.finished_at)}</>}
              </div>
            </div>
            {isSuperAdmin && <button onClick={showLog} className="flex items-center gap-1 text-xs text-nord-8 hover:underline"><ScrollText className="h-3.5 w-3.5" /> Log</button>}
          </div>
          {job.status === 'installing' && (
            <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-nord-2">
              <div className="h-full bg-nord-8 transition-all" style={{ width: `${Math.max(5, (Number(job.phase?.[0] ?? 0) / 6) * 100)}%` }} />
            </div>
          )}
        </div>
      )}

      {/* Offer */}
      {offer ? (
        <div className="rounded-xl border border-nord-8/50 bg-nord-1 p-5">
          <div className="flex items-center gap-3">
            <Download className="h-5 w-5 text-nord-8" />
            <div className="flex-1">
              <div className="font-semibold text-nord-6">Update available: <span className="font-mono">{offer.version}</span></div>
              <div className="text-xs text-nord-4">
                {offer.source === 'upload' ? 'uploaded bundle, signature verified' : <>published {when(offer.published_at)}</>}
                {offer.bundle?.size ? <> · {mb(offer.bundle.size)}{offer.bundle.from_version ? ` (delta from ${offer.bundle.from_version})` : ''}</> : null}
              </div>
            </div>
          </div>
          {offer.notes && <pre className="mt-4 max-h-64 overflow-auto whitespace-pre-wrap rounded-lg bg-nord-0 p-3 text-xs text-nord-4">{offer.notes}</pre>}
          {!offer.installable && <p className="mt-3 text-sm text-nord-12">Can't be installed automatically: {offer.reason}</p>}
          {status?.schedule ? (
            <div className="mt-4 flex items-center gap-3 text-sm text-nord-5">
              <Clock className="h-4 w-4 text-nord-8" /> Scheduled for {when(status.schedule.at)}
              {isSuperAdmin && <button disabled={!!busy} onClick={() => act('unschedule', () => api('/schedule', { method: 'DELETE' }))} className="text-xs text-nord-11 hover:underline">Cancel</button>}
            </div>
          ) : isSuperAdmin && offer.installable && !running && (
            <div className="mt-4 flex flex-wrap gap-3">
              <button disabled={!!busy} onClick={() => install(offer.version)}
                className="rounded-lg bg-nord-8 px-4 py-2 text-sm font-semibold text-nord-0 hover:bg-nord-7 disabled:opacity-50">
                {busy === 'install' ? 'Starting…' : 'Install now'}
              </button>
              {offer.source === 'channel' && (
                <button disabled={!!busy} onClick={() => schedule(offer.version)}
                  className="rounded-lg border border-nord-3 px-4 py-2 text-sm text-nord-5 hover:bg-nord-2 disabled:opacity-50">
                  Install tonight at 02:00
                </button>
              )}
            </div>
          )}
          {isSuperAdmin && offer.installable && !running && !status?.schedule && (
            <p className="mt-3 text-xs text-nord-3">Takes a few minutes; users see about one minute of interruption. A backup is taken first and a failed update rolls back by itself.</p>
          )}
        </div>
      ) : status && !running && !unreachable && (
        <div className="flex items-center gap-3 rounded-xl border border-nord-2 bg-nord-1 p-5 text-sm text-nord-5">
          <CheckCircle2 className="h-5 w-5 text-nord-14" />
          This server is up to date{status.last_check ? ` (last checked ${when(status.last_check)})` : ''}.
        </div>
      )}

      {!isSuperAdmin && <p className="text-xs text-nord-3">Only super administrators can install updates.</p>}

      {/* Offline: upload a bundle */}
      {isSuperAdmin && !running && (
        <div className="rounded-xl border border-dashed border-nord-3 p-5">
          <div className="flex items-center gap-3">
            <Upload className="h-5 w-5 text-nord-4" />
            <div className="flex-1">
              <div className="text-sm font-medium text-nord-5">Upload an update file</div>
              <div className="text-xs text-nord-4">For servers without internet: an <span className="font-mono">eurion-update-*.tar</span> from Eurion. Only Eurion-signed files are accepted.</div>
            </div>
            <input ref={fileRef} type="file" accept=".tar" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) startUpload(f); e.target.value = ''; }} />
            <button disabled={!!upload} onClick={() => fileRef.current?.click()}
              className="rounded-lg border border-nord-3 px-3 py-1.5 text-sm text-nord-5 hover:bg-nord-2 disabled:opacity-50">Choose file</button>
          </div>
          {upload && (
            <div className="mt-3">
              <div className="mb-1 text-xs text-nord-4">{upload.pct < 100 ? `Uploading ${upload.name}: ${upload.pct}%` : 'Checking signature…'}</div>
              <div className="h-1.5 overflow-hidden rounded-full bg-nord-2"><div className="h-full bg-nord-8" style={{ width: `${upload.pct}%` }} /></div>
            </div>
          )}
        </div>
      )}

      {/* Password confirmation (step-up) */}
      {askPassword && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <form className="w-80 rounded-xl border border-nord-2 bg-nord-1 p-5" onSubmit={(e) => { e.preventDefault(); void confirmPassword(); }}>
            <div className="font-semibold text-nord-6">Confirm your password</div>
            <p className="mt-1 text-xs text-nord-4">Required before changing what runs on this server.</p>
            <input type="password" autoFocus value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password"
              className="mt-3 w-full rounded-lg border border-nord-3 bg-nord-0 px-3 py-2 text-sm text-nord-6 outline-none focus:border-nord-8" />
            {pwError && <p className="mt-2 text-xs text-nord-11">{pwError}</p>}
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setAskPassword(null)} className="rounded-lg px-3 py-1.5 text-sm text-nord-4 hover:bg-nord-2">Cancel</button>
              <button type="submit" disabled={!password} className="rounded-lg bg-nord-8 px-3 py-1.5 text-sm font-semibold text-nord-0 disabled:opacity-50">Confirm</button>
            </div>
          </form>
        </div>
      )}

      {/* Log */}
      {log !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-8" onClick={() => setLog(null)}>
          <pre className="max-h-full w-full max-w-4xl overflow-auto rounded-xl bg-nord-0 p-4 text-xs text-nord-4" onClick={(e) => e.stopPropagation()}>{log}</pre>
        </div>
      )}
    </div>
  );
}
